import { useState, useEffect, useRef, useCallback } from "react";
import { motion } from "framer-motion";
import {
  Upload, Loader2, Trash2, X, Images, GripVertical, Image as ImageIcon,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Field } from "./_atoms";

export function SingleMediaField({ value, onChange, onMultiSelected }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const abortRef = useRef(null);

  const handleFiles = (files) => {
    const arr = Array.from(files || []);
    if (arr.length === 0) return;
    if (arr.length >= 2 && onMultiSelected) {
      // Delegate multi-file case up: parent will switch to carousel + trigger upload
      onMultiSelected(arr);
      return;
    }
    doUpload(arr[0]);
  };

  const doUpload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    const controller = new AbortController();
    abortRef.current = controller;
    setUploading(true); setProgress(0);
    try {
      const r = await api.post("/media/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        signal: controller.signal,
        onUploadProgress: (e) => {
          if (e.total) setProgress(Math.round((e.loaded / e.total) * 100));
        },
      });
      onChange({ url: r.data.public_url, kind: r.data.kind });
      toast.success(`${r.data.kind === "image" ? "Foto" : "Video"} caricato ✓`);
    } catch (e) {
      if (controller.signal.aborted) toast.info("Upload annullato");
      else toast.error(e?.response?.data?.detail || "Upload fallito");
    }
    setUploading(false); setProgress(0);
    abortRef.current = null;
  };

  const cancelUpload = () => {
    abortRef.current?.abort();
  };

  const remove = () => onChange(null);

  return (
    <Field label="Foto o video" icon={ImageIcon}>
      {value ? (
        <div className="flex items-center gap-3 p-2 rounded-xl border border-[color:var(--sp-border)] bg-white">
          {value.kind === "video" ? (
            <video src={value.url} className="h-16 w-16 rounded-lg object-cover" />
          ) : (
            <img src={value.url} alt="preview" className="h-16 w-16 rounded-lg object-cover" />
          )}
          <div className="flex-1 min-w-0">
            <div className="text-xs font-bold truncate">{value.kind === "video" ? "Video caricato" : "Foto caricata"}</div>
            <div className="text-[10px] text-[color:var(--sp-text-muted)] truncate">{value.url.split("/").pop()}</div>
          </div>
          <button type="button" onClick={remove}
            data-testid="publish-media-remove-btn"
            className="inline-flex items-center gap-1 px-2 py-1 text-xs text-red-600 hover:bg-red-50 rounded-md transition-colors">
            <Trash2 className="h-3.5 w-3.5" /> Rimuovi
          </button>
        </div>
      ) : uploading ? (
        <div className="flex items-center gap-3 p-3 rounded-xl border border-[color:var(--sp-border)] bg-white">
          <Loader2 className="h-5 w-5 animate-spin text-[color:var(--sp-brand)]" />
          <div className="flex-1">
            <div className="text-xs font-bold">Caricamento… {progress}%</div>
            <div className="mt-1 h-1.5 bg-neutral-100 rounded-full overflow-hidden">
              <div className="h-full bg-[color:var(--sp-brand)] transition-all" style={{ width: `${progress}%` }} />
            </div>
          </div>
          <button type="button" onClick={cancelUpload}
            data-testid="publish-media-cancel-btn"
            className="inline-flex items-center gap-1 px-2 py-1 text-xs text-red-600 hover:bg-red-50 rounded-md transition-colors">
            <X className="h-3.5 w-3.5" /> Annulla
          </button>
        </div>
      ) : (
        <button type="button" onClick={() => inputRef.current?.click()}
          data-testid="publish-media-upload-btn"
          className="w-full flex flex-col items-center justify-center gap-2 px-4 py-8 rounded-xl border-2 border-dashed border-[color:var(--sp-border)] hover:border-[color:var(--sp-brand)] hover:bg-[color:var(--sp-subtle)]/40 transition-colors">
          <Upload className="h-6 w-6 text-[color:var(--sp-text-muted)]" />
          <div className="text-xs font-bold text-[color:var(--sp-text-secondary)]">Carica foto o video</div>
          <div className="text-[10px] text-[color:var(--sp-text-muted)]">JPG, PNG, GIF, WEBP, MP4, MOV — max 100MB</div>
        </button>
      )}
      <input ref={inputRef} type="file" accept="image/*,video/*" multiple className="hidden"
        data-testid="publish-media-file-input"
        onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }} />
    </Field>
  );
}
export function MultiMediaUploader({ value = [], onChange, pendingFiles = null,
                                    onPendingConsumed, onUploadingChange }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const abortRef = useRef(null);
  const [dragIdx, setDragIdx] = useState(null);
  const startedRef = useRef(false);
  const max = 10;

  useEffect(() => {
    if (uploading) { startedRef.current = true; onUploadingChange?.(true); }
    else if (startedRef.current) onUploadingChange?.(false);
  }, [uploading, onUploadingChange]);

  const handleFiles = useCallback(async (files) => {
    const remaining = max - value.length;
    const list = Array.from(files || []).slice(0, remaining);
    if (list.length === 0) return;

    const controller = new AbortController();
    abortRef.current = controller;
    setUploading(true);
    // Upload in parallelo ma esito indicizzato: le miniature restano NELL'ORDINE
    // in cui i file sono stati selezionati, indipendentemente da chi finisce prima.
    let done = 0;
    const slots = await Promise.all(list.map(async (f) => {
      try {
        const fd = new FormData();
        fd.append("file", f);
        const r = await api.post("/media/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          signal: controller.signal,
        });
        return { url: r.data.public_url, kind: r.data.kind, name: f.name };
      } catch (e) {
        if (!controller.signal.aborted) toast.error(`${f.name}: upload fallito`);
        return null;
      } finally {
        done += 1;
        setProgress(Math.round((done / list.length) * 100));
      }
    }));
    const uploaded = slots.filter(Boolean);
    onChange([...value, ...uploaded]);
    setUploading(false); setProgress(0);
    abortRef.current = null;
    if (uploaded.length) toast.success(`${uploaded.length} media aggiunti al carousel`);
  }, [value, onChange]);

  // Carica i file selezionati nel campo singolo (hand-off dal parent via prop).
  // ``onPendingConsumed`` viene chiamato SOLO a upload finito: il parent resta
  // consapevole che l'operazione è in corso e non torna al media singolo.
  const consumedRef = useRef(null);
  useEffect(() => {
    if (!pendingFiles || pendingFiles === consumedRef.current) return;
    consumedRef.current = pendingFiles;
    handleFiles(pendingFiles).finally(() => onPendingConsumed?.());
  }, [pendingFiles, handleFiles, onPendingConsumed]);

  const cancelUpload = () => abortRef.current?.abort();

  const removeAt = (i) => {
    const next = value.slice();
    next.splice(i, 1);
    onChange(next);
  };

  // HTML5 drag & drop con riordino LIVE: le miniature si spostano mentre trascini
  const onDragStart = (idx) => (e) => {
    setDragIdx(idx);
    e.dataTransfer.effectAllowed = "move";
    try { e.dataTransfer.setData("text/plain", String(idx)); } catch { }
  };
  const moveTo = (idx) => {
    if (dragIdx == null || dragIdx === idx) return;
    const next = value.slice();
    const [moved] = next.splice(dragIdx, 1);
    next.splice(idx, 0, moved);
    setDragIdx(idx);
    onChange(next);
  };
  const onDragOver = (idx) => (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    moveTo(idx);
  };
  const onDrop = (idx) => (e) => {
    e.preventDefault();
    moveTo(idx);
    setDragIdx(null);
  };
  const onDragEnd = () => setDragIdx(null);

  return (
    <Field label={`Carousel (${value.length}/${max}) — trascina per riordinare`} icon={Images}>
      <div className="grid grid-cols-5 gap-2" data-testid="publish-carousel-grid"
           onDragOver={(e) => e.preventDefault()}>
        {value.map((m, i) => (
          <motion.div key={m.url}
            layout
            transition={{ type: "spring", stiffness: 520, damping: 34, mass: 0.6 }}
            animate={{ scale: dragIdx === i ? 1.06 : 1, opacity: dragIdx === i ? 0.85 : 1 }}
            draggable
            onDragStart={onDragStart(i)}
            onDragOver={onDragOver(i)}
            onDragEnter={onDragOver(i)}
            onDrop={onDrop(i)}
            onDragEnd={onDragEnd}
            data-testid={`publish-carousel-item-${i}`}
            data-name={m.name || ""}
            title={m.name || ""}
            className={`relative aspect-square rounded-lg overflow-hidden border group cursor-grab active:cursor-grabbing
              ${dragIdx === i
                ? "border-[color:var(--sp-brand)] ring-2 ring-[color:var(--sp-brand)]/50 shadow-lg z-10"
                : "border-[color:var(--sp-border)]"}`}>
            {m.kind === "video" ? (
              <video src={m.url} className="w-full h-full object-cover pointer-events-none" />
            ) : (
              <img src={m.url} alt={m.name || ""} className="w-full h-full object-cover pointer-events-none" />
            )}
            <div className="absolute top-1 left-1 h-5 w-5 rounded-full bg-black/60 text-white flex items-center justify-center opacity-70 group-hover:opacity-100 transition-opacity">
              <GripVertical className="h-3 w-3" />
            </div>
            <button type="button" onClick={() => removeAt(i)}
              data-testid={`publish-carousel-remove-${i}`}
              className="absolute top-1 right-1 h-5 w-5 rounded-full bg-black/70 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
              <X className="h-3 w-3" />
            </button>
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent text-white text-[10px] font-bold text-center py-0.5">
              {i + 1}
            </div>
          </motion.div>
        ))}
        {value.length < max && !uploading && (
          <button type="button" onClick={() => inputRef.current?.click()}
            data-testid="publish-carousel-add-btn"
            className="aspect-square rounded-lg border-2 border-dashed border-[color:var(--sp-border)] hover:border-[color:var(--sp-brand)] hover:bg-[color:var(--sp-subtle)]/40 flex flex-col items-center justify-center gap-1 text-[color:var(--sp-text-muted)] transition-colors">
            <Upload className="h-5 w-5" />
            <span className="text-[10px] font-bold">Aggiungi</span>
          </button>
        )}
        {uploading && (
          <div className="aspect-square rounded-lg border-2 border-[color:var(--sp-brand)] bg-[color:var(--sp-subtle)]/40 flex flex-col items-center justify-center gap-1 relative">
            <Loader2 className="h-5 w-5 animate-spin text-[color:var(--sp-brand)]" />
            <span className="text-[10px] font-bold text-[color:var(--sp-brand)]">{progress}%</span>
            <button type="button" onClick={cancelUpload}
              data-testid="publish-carousel-cancel-btn"
              className="absolute top-1 right-1 h-5 w-5 rounded-full bg-red-500 text-white flex items-center justify-center">
              <X className="h-3 w-3" />
            </button>
          </div>
        )}
      </div>
      <input ref={inputRef} type="file" accept="image/*,video/*" multiple className="hidden"
        data-testid="publish-carousel-file-input"
        onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }} />
      <div className="text-[10px] text-[color:var(--sp-text-muted)] mt-1">
        Minimo 2 media per pubblicare. Trascina le miniature per cambiare l'ordine.
      </div>
    </Field>
  );
}

// ==========================================================================
// Location picker — cerca luoghi Facebook (id compatibile con FB place & IG location_id)
